import { Autocomplete, Box, Button, Divider, Paper, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "../../components/common/DatePicker";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { useCancellableCeleryPoll } from "../../hooks/useCancellableCeleryPoll";
import { generateClassifiedSheet, getMasterCompanyNames } from "../../services/gsuite/sheets";
import { applyCeleryPollOutcomeToAlert } from "../../utils/celeryPollAlert";

/** Alert text for each terminal poll outcome (see applyCeleryPollOutcomeToAlert). */
const POLL_ALERT_COPY = {
  success: { severity: "success", headline: "Sheet generated successfully." },
  failed: { severity: "error", headline: "Sheet generation failed." },
  timeout: {
    severity: "warning",
    message:
      "Sheet generation is taking longer than expected. Check back later or retry.",
  },
  fallback: { severity: "error", message: "Could not verify sheet generation status." },
};

function SheetGenerator() {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useAlert();
  const pollTask = useCancellableCeleryPoll(); // aborts on unmount / re-submit

  const [company, setCompany] = useState(null);
  const [companyNames, setCompanyNames] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [sheetName, setSheetName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadCompanyNames = async () => {
      const response = await getMasterCompanyNames();
      if (response.status === 200) {
        const data = await response.json();
        const names = (data.company_names ?? []).slice().sort();
        setCompanyNames(names);
        return;
      }
      showAlert("error", "Could not load company names.");
    };
    loadCompanyNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (!company) {
      showAlert("error", "Please select a company.");
      return;
    }
    if (startDate >= endDate) {
      showAlert("error", "Start date must be before end date.");
      return;
    }
    if (!sheetName.trim()) {
      showAlert("error", "Please enter a sheet name.");
      return;
    }

    setIsSubmitting(true);
    showAlert("info", "Starting sheet generation...");

    try {
      // 1. Enqueue Celery job; backend returns task_id for status polling
      const response = await generateClassifiedSheet(
        company,
        dayjs(startDate).format("YYYY-MM-DD"),
        dayjs(endDate).format("YYYY-MM-DD"),
        sheetName.trim() || `${company}-classified-sheet`,
      );

      if (!(response instanceof Response)) {
        return;
      }

      const body = await response.json();
      if (!response.ok) {
        showAlert("error", body.error || "Could not start sheet generation.");
        return;
      }

      const taskId = body.task_id;
      if (!taskId) {
        showAlert("error", "Could not get task id for sheet generation.");
        return;
      }

      // 2. Poll Mongo task log until completed / failed / timeout
      showAlert("info", "Processing sheet...");
      const { outcome, result } = await pollTask(taskId);
      applyCeleryPollOutcomeToAlert(showAlert, outcome, result, POLL_ALERT_COPY);
    } catch {
      showAlert("error", POLL_ALERT_COPY.fallback.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      <TopBarGridContainer>
        <Grid>
          <Title text={"SHEET GENERATOR"} sx={{ my: 5 }} />
        </Grid>
        <Grid size="grow">
          <Box sx={{ display: "flex", justifyContent: "end", mt: 5, gap: 1 }}>
            <Button variant="outlined" color="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Generating..." : "Generate Sheet"}
            </Button>
            <Button variant="outlined" color="error" onClick={() => navigate("/tasks")}>
              Cancel
            </Button>
          </Box>
        </Grid>
      </TopBarGridContainer>
      <Divider variant="middle" />

      <Grid container direction="column" spacing={4} p={3} mx={"10%"}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Autocomplete
            options={companyNames}
            value={company}
            onChange={(event, value) => setCompany(value)}
            renderInput={(params) => <TextField {...params} label="Select Company" />}
            fullWidth
          />
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker currentVal={startDate} handleVal={setStartDate} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker currentVal={endDate} handleVal={setEndDate} />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <TextField
            label="Sheet Name"
            value={sheetName}
            onChange={(event) => setSheetName(event.target.value)}
            fullWidth
          />
        </Paper>
      </Grid>
    </Layout>
  );
}

export default SheetGenerator;
